import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Space, Tabs } from 'antd';
import { BarcodeFormat, BrowserBarcodeReader } from '@zxing/library';
import { TextSmall } from '../../text';
import { openAlert } from '../../alert/hooks/useAlert';
import { FaBarcode, FaCamera, FaImage } from 'react-icons/fa';
import { ImageTheme } from '../../images';
import exBarcode from '../../../assets/images/ex_take_barcode_photo.jpg';
import { cardStyle } from '../../../screens/registration-qr/styles/styles';

export const BarcodeScannerAutoModal = ({ open, onClose, onDetected }) => {
  const [scanResult, setScanResult] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [activeTab, setActiveTab] = useState('camera');
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const codeReaderRef = useRef(null);
  const streamRef = useRef(null);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    if (open) {
      setScanResult('');
      setIsScanning(false);
      setActiveTab('camera');
      isProcessingRef.current = false;
    } else {
      stopCamera();
      isProcessingRef.current = false;
    }
    return () => {
      stopCamera();
      isProcessingRef.current = false;
    };
  }, [open]);

  const stopCamera = () => {
    const stream = streamRef.current || videoRef.current?.srcObject;
    if (stream && typeof stream.getTracks === 'function') {
      stream.getTracks().forEach((track) => track.stop());
    }
    streamRef.current = null;
    if (codeReaderRef.current) {
      codeReaderRef.current.reset();
      codeReaderRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const startCameraScan = async () => {
    try {
      setIsScanning(true);
      stopCamera();

      const codeReader = new BrowserBarcodeReader();
      codeReaderRef.current = codeReader;

      // ใช้เฉพาะกล้องหลัง (environment = rear camera บนมือถือ)
      const constraints = {
        video: { facingMode: { exact: 'environment' } },
      };

      // Start scanning จากกล้องหลัง
      const result = await codeReader.decodeFromConstraints(
        constraints,
        videoRef.current,
        (result, error) => {
          if (result && !isProcessingRef.current) {
            // Prevent multiple calls
            isProcessingRef.current = true;

            const scannedCode = result.getText();
            const format = result.getBarcodeFormat();

            // Check if it's EAN_13 format (or other supported formats)
            if (
              format === BarcodeFormat.EAN_13 ||
              format === BarcodeFormat.EAN_8 ||
              format === BarcodeFormat.CODE_128
            ) {
              // Extract last 9 digits from barcode
              // EAN-13 format: 8 859095 812348 (13 digits total)
              // We need last 9 digits: 590958123 (from position 4-12)
              let extractedCode = scannedCode;
              setScanResult(extractedCode);
              setIsScanning(false);
              stopCamera();
              onDetected(extractedCode);
              onClose();
            } else {
              openAlert({
                type: 'error',
                message: `รูปแบบบาร์โค้ด ${format} ไม่รองรับ กรุณาใช้บาร์โค้ด EAN-13, EAN-8 หรือ CODE-128`,
              });
              stopCamera();
              setIsScanning(false);
              isProcessingRef.current = false;
            }
          }
          // Ignore expected "no barcode" errors (NotFoundException or undefined message from continuous decode)
          const isNotFound =
            (error && error.name === 'NotFoundException') ||
            (error && (error.message === undefined || error.message === ''));
          if (error && !isNotFound) {
            console.error('Scan error:', error);
            stopCamera();
            setIsScanning(false);
            isProcessingRef.current = false;
            openAlert({
              type: 'error',
              message: 'เกิดข้อผิดพลาดในการสแกน กรุณาลองใหม่อีกครั้ง',
            });
          }
        },
      );

      // Store the stream for cleanup
      if (videoRef.current && videoRef.current.srcObject) {
        streamRef.current = videoRef.current.srcObject;
      }
    } catch (error) {
      console.error('Camera error:', error);
      stopCamera();
      setIsScanning(false);
      isProcessingRef.current = false;
      const isOverconstrained =
        error?.name === 'OverconstrainedError' || error?.message?.includes('environment');
      openAlert({
        type: 'error',
        message: isOverconstrained
          ? 'ไม่พบกล้องหลัง หรืออุปกรณ์ไม่รองรับ กรุณาใช้มือถือที่มีกล้องหลังในการสแกนบาร์โค้ด'
          : 'ไม่สามารถเข้าถึงกล้องได้ กรุณาตรวจสอบว่าเบราว์เซอร์ได้รับอนุญาตให้เข้าถึงกล้องหรือไม่',
      });
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || isProcessingRef.current) return;

    try {
      isProcessingRef.current = true;
      setIsScanning(true);
      const codeReader = new BrowserBarcodeReader();

      const reader = new FileReader();
      reader.onload = async () => {
        const img = new Image();
        img.src = reader.result;
        img.onload = async () => {
          try {
            const result = await codeReader.decodeFromImageElement(img, {
              tryHarder: true,
            });

            const scannedCode = result.getText();
            const format = result.getBarcodeFormat();

            // Check if it's EAN_13 format (or other supported formats)
            if (
              format === BarcodeFormat.EAN_13 ||
              format === BarcodeFormat.EAN_8 ||
              format === BarcodeFormat.CODE_128
            ) {
              // Extract last 9 digits from barcode
              // EAN-13 format: 8 859095 812348 (13 digits total)
              // We need last 9 digits: 590958123 (from position 4-12)
              let extractedCode = scannedCode;

              setScanResult(extractedCode);
              onDetected(extractedCode);
              onClose();
            } else {
              openAlert({
                type: 'error',
                message: `รูปแบบบาร์โค้ด ${format} ไม่รองรับ กรุณาใช้บาร์โค้ด EAN-13, EAN-8 หรือ CODE-128`,
              });
            }
          } catch (error) {
            openAlert({
              type: 'error',
              message: 'ไม่สามารถอ่านบาร์โค้ดจากภาพนี้ได้ กรุณาลองใหม่อีกครั้ง',
            });
            setIsScanning(false);
            isProcessingRef.current = false;
          } finally {
            setIsScanning(false);
            isProcessingRef.current = false;
          }
        };
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Image scan error:', error);
      stopCamera();
      setIsScanning(false);
      isProcessingRef.current = false;
      openAlert({
        type: 'error',
        message: 'เกิดข้อผิดพลาดในการอ่านบาร์โค้ด',
      });
    }
  };

  const handleOpenFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === 'camera') {
      stopCamera();
    }
  };

  const tabItems = [
    {
      key: 'camera',
      label: (
        <Space>
          <FaCamera />
          ใช้กล้อง
        </Space>
      ),
      children: (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={cardStyle}>
            <TextSmall
              text={<span>ตั้งกล้องมือถือในแนวตั้ง เพื่อถ่าย Barcode</span>}
              style={{ color: '#8c6e19' }}
              bold
            />
          </div>
          <div
            style={{
              width: '100%',
              maxWidth: '500px',
              margin: '20px auto',
              position: 'relative',
              backgroundColor: '#fff',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            <video
              ref={videoRef}
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
              playsInline
            />
            {!isScanning && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  backgroundColor: 'rgba(216, 216, 216, 0.7)',
                  color: '#000',
                }}
              >
                <FaBarcode
                  style={{
                    fontSize: 56,
                    color: '#ff4d4f',
                    transform: 'scaleX(1.8)',
                  }}
                />
                <TextSmall text="สแกนบาร์โค้ด" style={{ color: '#000', fontWeight: 600 }} />
                <TextSmall text="กดปุ่มเริ่มสแกนเพื่อเปิดกล้อง" style={{ color: '#666' }} />
              </div>
            )}
          </div>
          <Button
            type="primary"
            size="large"
            icon={<FaBarcode />}
            onClick={startCameraScan}
            loading={isScanning}
            disabled={isScanning}
            style={{ width: '100%', maxWidth: '300px' }}
          >
            {isScanning ? 'กำลังสแกน...' : 'เริ่มสแกนบาร์โค้ด'}
          </Button>
          {isScanning && (
            <Button
              style={{ marginTop: '10px', width: '100%', maxWidth: '300px' }}
              onClick={() => {
                stopCamera();
                setIsScanning(false);
                isProcessingRef.current = false;
              }}
            >
              หยุดสแกน
            </Button>
          )}
        </div>
      ),
    },
    {
      key: 'upload',
      label: (
        <Space>
          <FaImage />
          อัปโหลดรูปภาพ
        </Space>
      ),
      children: (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={cardStyle}>
            <TextSmall
              text={<span>ตั้งกล้องมือถือในแนวตั้ง เพื่อถ่าย Barcode</span>}
              style={{ color: '#8c6e19' }}
              bold
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
            <ImageTheme
              src={exBarcode}
              style={{
                width: '100%',
                maxWidth: 400,
                borderRadius: '5px',
                display: 'block',
                margin: 'auto',
              }}
            />
          </div>
          <TextSmall
            text="เลือกรูปภาพที่มีบาร์โค้ดเพื่อสแกน"
            style={{ marginBottom: '20px', display: 'block' }}
          />
          <input
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            ref={fileInputRef}
            onChange={handleImageUpload}
          />
          <Button
            type="primary"
            size="large"
            icon={<FaImage />}
            onClick={handleOpenFilePicker}
            loading={isScanning}
            style={{ width: '100%', maxWidth: '300px' }}
          >
            เลือกรูปภาพ
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <FaBarcode />
          สแกนบาร์โค้ด
        </Space>
      }
      style={{ top: '5%' }}
      open={open}
      onCancel={() => {
        stopCamera();
        onClose();
      }}
      footer={null}
      width={600}
    >
      <Tabs activeKey={activeTab} items={tabItems} onChange={handleTabChange} />
      {scanResult && (
        <div
          style={{
            marginTop: '20px',
            padding: '10px',
            backgroundColor: '#f0f0f0',
            borderRadius: '4px',
          }}
        >
          <TextSmall text={`บาร์โค้ดที่พบ: `} bold />
          <TextSmall text={scanResult} style={{ color: '#1890ff' }} />
        </div>
      )}
    </Modal>
  );
};
